import React, { useState, useEffect, useRef } from "react";
import { usePlayer, usePlayers, useGame, useStage, useRound } from "@empirica/core/player/classic/react";
import { getNicknameFromId, getAnimalFromId, getAnimalEmoji, getAnimalFromPlayer, getNicknameFromPlayer } from "../components/Avatar.jsx";
import { getTaskConfig } from "../utils/taskConfig.js";
import { recordSubmittedIntegrityText } from "../utils/integrityTracking.js";
import { useChatAutoScroll } from "../utils/useChatAutoScroll.js";
import { savePlayerMeta } from "../utils/savePlayerMeta.js";
import {
  getCompensationConfig,
  isBaseRateStaticSyntheticGroupCondition,
} from "../utils/conditionUtils.js";

const CONVINCINGNESS_SCALE = [
  { value: 1, label: "Not at all" },
  { value: 2, label: "Slightly" },
  { value: 3, label: "Moderately" },
  { value: 4, label: "Very" },
  { value: 5, label: "Extremely" },
];

export function Discussion() {
  const player = usePlayer();
  const players = usePlayers();
  const game = useGame();
  const stage = useStage();
  const round = useRound();
  const treatment = game.get("treatment") || {};
  const isBaseRateStaticSyntheticGroup =
    isBaseRateStaticSyntheticGroupCondition(treatment);
  const [hasReviewedGroupRationales, setHasReviewedGroupRationales] = useState(
    () => player.stage.get("hasReviewedGroupRationales") === true
  );
  const [peerConvincingnessRatings, setPeerConvincingnessRatings] = useState(() => {
    const stored =
      player.stage.get("peerConvincingnessRatings") ||
      player.get("peerConvincingnessRatings") ||
      {};
    return typeof stored === "object" && stored !== null ? stored : {};
  });
  // Input text persists across turn changes - only cleared when message is successfully sent
  const [inputText, setInputText] = useState("");
  // Current time state for live countdown display
  const [currentTime, setCurrentTime] = useState(Date.now());
  // Toggle showing the loan case panel
  const [showLoanCase, setShowLoanCase] = useState(
    () => !isBaseRateStaticSyntheticGroupCondition(treatment)
  );
  const staticSyntheticPeersRaw = isBaseRateStaticSyntheticGroup
    ? stage.get("staticSyntheticPeers") ||
      round.get("staticSyntheticPeers") ||
      game.get("staticSyntheticPeers") ||
      []
    : [];
  const staticSyntheticPeers = Array.isArray(staticSyntheticPeersRaw)
    ? staticSyntheticPeersRaw
    : [];
  const taskConfig = getTaskConfig(treatment);
  const compensation = getCompensationConfig(treatment);
  const DetailsComponent = taskConfig.DetailsComponent;
  const detailsToggle = taskConfig.detailsToggle || {};
  const detailsTitle = taskConfig.detailsTitle || "Case details";
  const playerBeliefs = game.get("playerBeliefs") || {};
  const discussionParticipants = [
    ...players.map((participant) => ({
      id: participant.id,
      isSynthetic: false,
      player: participant,
    })),
    ...staticSyntheticPeers.map((peer) => ({
      id: peer.id,
      isSynthetic: true,
      peer,
    })),
  ];
  const formatBelief = (value) => {
    if (typeof taskConfig.formatPredictionDisplay === "function") {
      return taskConfig.formatPredictionDisplay(value);
    }
    return String(value ?? "").trim();
  };
  const tType = treatment.treatmentType ||
    ((treatment.name || "").toLowerCase().includes("no-ai") ? "No-AI Control" :
     (treatment.name || "").toLowerCase().includes("nogroup") ? "nogroup" :
     (treatment.name || "").toLowerCase().includes("control") ? "Control" : undefined);
  const stageTranscript = stage.get("messages") ?? [];
  const stageMessages = Array.isArray(stageTranscript?.messages)
    ? stageTranscript.messages
    : Array.isArray(stageTranscript)
    ? stageTranscript
    : [];
  const [forceScrollKey, setForceScrollKey] = useState(0);
  
  // Turn-taking logic
  const turnTakingEnabled = stage.get("turnTakingEnabled") || false;
  const turnOrder = stage.get("turnOrder") || [];
  const currentTurnIndex = stage.get("currentTurnIndex") || 0;
  const currentTurnPlayerId = turnOrder.length > 0 ? turnOrder[currentTurnIndex] : null;
  const isMyTurn = turnTakingEnabled ? (currentTurnPlayerId === player.id) : true;
  const turnStartTime = stage.get("turnStartTime") || Date.now();
  const discussionPrepEnabled = stage.get("discussionPrepEnabled") === true;
  const discussionPrepEndsAt = Number(stage.get("discussionPrepEndsAt") || 0);
  const prepRemainingMs = discussionPrepEnabled
    ? Math.max(0, discussionPrepEndsAt - currentTime)
    : 0;
  const isPrepWindowActive = discussionPrepEnabled && prepRemainingMs > 0;
  const canSendMessage = !isPrepWindowActive && (!turnTakingEnabled || isMyTurn);

  const formatCountdown = (milliseconds) => {
    const totalSeconds = Math.max(0, Math.ceil(milliseconds / 1000));
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };
  
  // Get current turn player's name for display
  const currentTurnPlayer = currentTurnPlayerId
    ? players.find(p => p.id === currentTurnPlayerId)
    : null;
  const currentTurnPlayerName = currentTurnPlayer
    ? getNicknameFromPlayer(currentTurnPlayer)
    : null;

  // Get next turn player's name for reminder
  const nextTurnIndex = turnOrder.length > 0 ? (currentTurnIndex + 1) % turnOrder.length : -1;
  const nextTurnPlayerId = nextTurnIndex >= 0 && turnOrder.length > 0 ? turnOrder[nextTurnIndex] : null;
  const nextTurnPlayer = nextTurnPlayerId
    ? players.find(p => p.id === nextTurnPlayerId)
    : null;
  const nextTurnPlayerName = nextTurnPlayer
    ? getNicknameFromPlayer(nextTurnPlayer)
    : null;
  const isNextSpeaker = nextTurnPlayerId === player.id;

  // Timer ref to track the interval
  const timerRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const lastTurnIndexRef = useRef(currentTurnIndex);
  const lastTurnStartTimeRef = useRef(turnStartTime);
  const {
    containerRef: messagesContainerRef,
    endRef: messagesEndRef,
    handleScroll,
  } = useChatAutoScroll({
    itemCount: stageMessages.length,
    forceScrollKey,
  });

  // Update current time every second for live countdown display
  useEffect(() => {
    if (!turnTakingEnabled && !discussionPrepEnabled) {
      setCurrentTime(Date.now());
      return;
    }

    setCurrentTime(Date.now());
    countdownTimerRef.current = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);

    return () => {
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
    };
  }, [discussionPrepEnabled, turnTakingEnabled]);

  // Auto-advance turn after 45 seconds
  useEffect(() => {
    if (!turnTakingEnabled) return;

    // Check if turn index or start time changed
    const turnChanged = lastTurnIndexRef.current !== currentTurnIndex || 
                        lastTurnStartTimeRef.current !== turnStartTime;
    
    if (turnChanged) {
      lastTurnIndexRef.current = currentTurnIndex;
      lastTurnStartTimeRef.current = turnStartTime;
      
      // Clear any existing timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }

    // Set up timer to check if 45 seconds have passed
    const checkTurnTimeout = () => {
      // Get fresh values from stage to avoid stale closures
      const currentIndex = stage.get("currentTurnIndex") || 0;
      const startTime = stage.get("turnStartTime") || Date.now();
      
      // Only proceed if this is still the same turn we're tracking
      if (currentIndex !== currentTurnIndex || startTime !== turnStartTime) {
        return; // Turn has changed, timer will be reset by useEffect
      }
      
      const now = Date.now();
      const elapsed = now - startTime;
      const timeLimit = 45000; // 45 seconds in milliseconds

      if (elapsed >= timeLimit) {
        // Time limit reached, advance to next turn
        const turnOrder = stage.get("turnOrder") || [];
        if (turnOrder.length > 0) {
          const nextIndex = (currentIndex + 1) % turnOrder.length;
          stage.set("currentTurnIndex", nextIndex);
          stage.set("turnStartTime", Date.now());
          console.info("[TURN-TAKING] Turn auto-advanced after 45 seconds:", {
            from: turnOrder[currentIndex],
            to: turnOrder[nextIndex],
            index: nextIndex
          });
        }
      }
    };

    // Check immediately and then every second
    checkTurnTimeout();
    timerRef.current = setInterval(checkTurnTimeout, 1000);

    // Cleanup on unmount or when dependencies change
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [turnTakingEnabled, currentTurnIndex, turnStartTime, stage]);

  const handleSendMessage = () => {
    if (isBaseRateStaticSyntheticGroup) return;
    if (isPrepWindowActive) return;
    if (!isMyTurn && turnTakingEnabled) return;

    const text = inputText.trim();
    if (!text) return;

    const timestamp = Date.now();
    const msg = {
      id: `usr-${timestamp}`,
      role: "user",
      text,
      createdAt: timestamp,
      subjectId: player.id,
      subject: { id: player.id, name: getNicknameFromPlayer(player) },
    };

    const stageMessages = stage.get("messages");
    const stageArray = Array.isArray(stageMessages?.messages)
      ? stageMessages.messages
      : Array.isArray(stageMessages)
      ? stageMessages
      : [];
    const updatedStageMessages = Array.isArray(stageMessages?.messages)
      ? { ...stageMessages, messages: [...stageArray, msg] }
      : [...stageArray, msg];
    stage.set("messages", updatedStageMessages);

    try {
      const roundMessages = stage.currentRound?.get("messages");
      const roundArray = Array.isArray(roundMessages?.messages)
        ? roundMessages.messages
        : Array.isArray(roundMessages)
        ? roundMessages
        : [];
      stage.currentRound?.set("messages", [...roundArray, msg]);
    } catch (_) {}

    recordSubmittedIntegrityText({
      playerId: player.id,
      fieldId: "discussion-message-input",
      text,
      phase: "in-game",
      stageId: stage?.id,
      stageName: stage?.get("name"),
      detectedAt: timestamp,
    });

    try {
      const events = round.get("eventLog") || [];
      events.push({ t: timestamp, type: "message", stageId: stage?.id, stageName: stage?.get("name"), playerId: player.id, role: "user", text });
      round.set("eventLog", events);
    } catch (_) {}

    setForceScrollKey((prev) => prev + 1);
    setInputText("");
  };

  const handleContinue = () => {
    if (
      isBaseRateStaticSyntheticGroup &&
      (!hasReviewedGroupRationales || !hasRatedAllPeers)
    ) {
      return;
    }

    const timestamp = Date.now();
    player.stage.set("submit", true);
    player.stage.set("submittedAt", timestamp);
    try {
      const events = round.get("eventLog") || [];
      events.push({
        t: timestamp,
        type: "click",
        stageId: stage?.id,
        stageName: stage?.get("name"),
        playerId: player.id,
        action: "continue_discussion",
      });
      round.set("eventLog", events);
    } catch (_) {}
  };

  const handleReviewedGroupRationalesChange = (event) => {
    const checked = event.target.checked;
    const timestamp = Date.now();

    setHasReviewedGroupRationales(checked);
    player.stage.set("hasReviewedGroupRationales", checked);
    player.stage.set(
      "reviewedGroupRationalesAt",
      checked ? timestamp : null
    );

    try {
      const events = round.get("eventLog") || [];
      events.push({
        t: timestamp,
        type: "toggle",
        stageId: stage?.id,
        stageName: stage?.get("name"),
        playerId: player.id,
        action: "reviewed_group_rationales",
        value: checked,
      });
      round.set("eventLog", events);
    } catch (_) {}
  };

  const syntheticPeerIds = staticSyntheticPeers.map((peer) => String(peer.id));
  const hasRatedAllPeers =
    !isBaseRateStaticSyntheticGroup ||
    syntheticPeerIds.length === 0 ||
    syntheticPeerIds.every((pid) =>
      Number.isFinite(Number(peerConvincingnessRatings[pid]))
    );

  const handleRatePeer = (peerId, score) => {
    const pid = String(peerId);
    const numericScore = Number(score);
    if (!Number.isFinite(numericScore)) return;
    const timestamp = Date.now();
    const next = { ...peerConvincingnessRatings, [pid]: numericScore };
    setPeerConvincingnessRatings(next);
    player.stage.set("peerConvincingnessRatings", next);
    player.stage.set("peerConvincingnessRatingsAt", timestamp);
    try {
      player.set("peerConvincingnessRatings", next);
      player.set("peerConvincingnessRatingsAt", timestamp);
    } catch (_) {}
    try {
      savePlayerMeta({
        player,
        name: "peerConvincingnessRatings",
        value: { ratings: next, updatedAt: timestamp },
        debugTag: "discussion: peer convincingness rating",
      });
    } catch (_) {}
    try {
      const events = round.get("eventLog") || [];
      events.push({
        t: timestamp,
        type: "rating",
        stageId: stage?.id,
        stageName: stage?.get("name"),
        playerId: player.id,
        action: "peer_convincingness_rating",
        peerId: pid,
        score: numericScore,
      });
      round.set("eventLog", events);
    } catch (_) {}
  };


  return (
    <div className="h-full min-h-0 w-full max-w-7xl mx-auto p-6 flex flex-col">
      <h2 className="text-2xl font-bold mb-6 shrink-0">
        {isBaseRateStaticSyntheticGroup ? "Group Review" : "Group Discussion"}
      </h2>

      <div className="flex-1 min-h-0 overflow-y-auto pr-1">
        <div className="space-y-6">
          {/* Combined deliberation and bonus information */}
          <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-6">
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Left: Deliberation message */}
              <div className="flex-1 space-y-3">
                <p className={`text-base text-gray-900 ${isBaseRateStaticSyntheticGroup ? "" : "font-semibold"}`}>
                  {isBaseRateStaticSyntheticGroup
                    ? (taskConfig.groupReviewHeadline ||
                      "Imagine you are making a group decision with two peers.")
                    : "You need to deliberate and try to convince others!"}
                </p>
                {isBaseRateStaticSyntheticGroup ? (
                  <p className="text-base text-gray-900">
                    {taskConfig.groupReviewSubtext ||
                      "Read their estimates and reasoning below. Think about how you would weigh their views against your own, what you find most persuasive, and what answer you would reach if you were deciding together as a group of three."}
                  </p>
                ) : (
                  <p className="text-sm text-gray-700">
                    {`After the discussion, we will ask every player to identify who in the group provided the most convincing reasons. The one player who receives the highest number of votes will earn an additional ${compensation.mostConvincingBonusText} bonus.`}
                  </p>
                )}
              </div>

              {/* Right: Turn-taking rules for Control and No-AI Control conditions */}
              {!isBaseRateStaticSyntheticGroup && (tType === "No-AI Control" || tType === "Control") && turnTakingEnabled && (
                <div className="flex-1 border-t-2 border-yellow-300 pt-6 lg:border-t-0 lg:border-l-2 lg:pt-0 lg:pl-6">
                  <p className="text-lg font-bold text-gray-900 mb-2">
                    Group Discussion Rules
                  </p>
                  <ul className="space-y-1 text-gray-700 text-sm list-disc list-inside">
                    {discussionPrepEnabled && (
                      <li><strong>2-minute prep window:</strong> Read the opening rationales and draft your first message before anyone can send.</li>
                    )}
                    <li><strong>Turn-taking:</strong> After prep, players speak in rotating order.</li>
                    <li><strong>45-second limit:</strong> You have 45 seconds per turn (timer shown).</li>
                    <li><strong>Auto-advance:</strong> Turn passes automatically if no message sent.</li>
                    <li><strong>Edit anytime:</strong> Type/edit anytime; send only on your turn.</li>
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Show case details for No-AI Control and Control conditions */}
          {(tType === "No-AI Control" || tType === "Control") && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-900">{detailsTitle}</h3>
                <button
                  type="button"
                  className="text-sm px-3 py-2 rounded-md font-semibold shadow-sm border border-empirica-500 text-white bg-empirica-600 hover:bg-empirica-700 focus:outline-none focus:ring-2 focus:ring-empirica-300"
                  onClick={() => setShowLoanCase((prev) => !prev)}
                >
                  {showLoanCase ? (detailsToggle.hide || "🙈 Hide case") : (detailsToggle.show || "👁️ Show case")}
                </button>
              </div>
              {showLoanCase ? (
                <div className="max-h-72 overflow-y-auto pr-2">
                  {DetailsComponent ? <DetailsComponent /> : null}
                </div>
              ) : (
                <div className="text-sm text-gray-600 border border-dashed border-gray-300 rounded-md p-3 bg-gray-50">
                  {detailsToggle.hiddenText || "Case hidden. Click \"Show case\" to view the details again."}
                </div>
              )}
            </div>
          )}

          <div className="bg-white rounded-lg shadow-md p-6 flex flex-col min-h-[32rem]">
            {taskConfig.discussionPrompt && (
            <p className="text-lg mb-4 shrink-0">
              {taskConfig.discussionPrompt}
            </p>
            )}

            {isPrepWindowActive && (
              <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4 shrink-0">
                <p className="text-base font-semibold text-amber-900">
                  Silent prep time: read the opening rationales and draft your first message.
                </p>
                <p className="mt-1 text-sm text-amber-800">
                  No one can send messages during this window. {currentTurnPlayerName || "The first speaker"} will speak first when prep ends.
                </p>
                <p className="mt-2 text-sm font-mono text-amber-900">
                  Prep remaining: {formatCountdown(prepRemainingMs)}
                </p>
              </div>
            )}

            {/* Two-panel layout */}
            <div className="flex-1 min-h-0 flex flex-col lg:flex-row gap-6 overflow-y-auto lg:overflow-hidden">
              {/* Left Panel - Profile and Initial Predictions */}
              {!isBaseRateStaticSyntheticGroup && (
              <div className="w-full lg:w-80 flex-shrink-0 lg:min-h-0 lg:overflow-y-auto lg:pr-1">
                {/* My Profile - Larger */}
                <div className="bg-gray-50 rounded-lg p-6 mb-6">
                  <h3 className="text-lg font-semibold mb-4">My Profile</h3>
                  <div className="flex items-center gap-4">
                    <div className="h-20 w-20 rounded-full bg-white shadow-lg flex items-center justify-center text-4xl">
                      <span aria-hidden>{getAnimalEmoji(getAnimalFromPlayer(player))}</span>
                    </div>
                    <div>
                      <div className="text-xl font-semibold text-gray-900">
                        {getNicknameFromPlayer(player)}
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        {(() => {
                          const playerBeliefs = game.get("playerBeliefs") || {};
                          const initialBelief = playerBeliefs[player.id]?.initialBelief;
                          const hasInitialBelief =
                            initialBelief !== undefined &&
                            initialBelief !== null &&
                            String(initialBelief).trim() !== "";
                          return hasInitialBelief ? (
                            <>
                              {isBaseRateStaticSyntheticGroup ? "Estimate" : "Predicts"}:{" "}
                              <span className="font-medium">{formatBelief(initialBelief)}</span>
                            </>
                          ) : null;
                        })()}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Initial Predictions */}
                {players.some((p) => {
                  const belief = playerBeliefs[p.id]?.initialBelief;
                  return belief !== undefined && belief !== null && String(belief).trim() !== "";
                }) || staticSyntheticPeers.some((peer) => String(peer?.initialBelief || "").trim() !== "") ? (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-lg font-semibold mb-3">
                      {isBaseRateStaticSyntheticGroup
                        ? "Your Estimate and Earlier Participant Estimates"
                        : "Group Predictions"}
                    </h3>
                    <div className="space-y-3">
                      {discussionParticipants.map((participant) => {
                        const nickname = participant.isSynthetic
                          ? participant.peer?.name || getNicknameFromId(participant.id)
                          : getNicknameFromPlayer(participant.player);
                        const animal = participant.isSynthetic
                          ? participant.peer?.assignedAnimal ||
                            getAnimalFromId(participant.id)
                          : getAnimalFromPlayer(participant.player);
                        const emoji = getAnimalEmoji(animal);
                        const belief = participant.isSynthetic
                          ? participant.peer?.initialBelief
                          : playerBeliefs[participant.id]?.initialBelief;
                        const hasBelief =
                          belief !== undefined &&
                          belief !== null &&
                          String(belief).trim() !== "";
                        if (!hasBelief) return null;
                        const isMe = participant.id === player.id;
                        return (
                          <div key={participant.id} className={`flex items-center gap-3 p-3 rounded-lg ${isMe ? 'bg-blue-100 border-2 border-blue-300' : 'bg-white shadow-sm'}`}>
                            <div className="h-12 w-12 rounded-full bg-white shadow flex items-center justify-center text-2xl">
                              <span aria-hidden>{emoji}</span>
                            </div>
                            <div className="flex-1">
                              <div className={`font-medium ${isMe ? 'text-blue-900' : 'text-gray-900'}`}>
                                {nickname} {isMe && '(You)'}
                              </div>
                              <div className={`text-sm ${isMe ? 'text-blue-700' : 'text-gray-500'}`}>
                                {isBaseRateStaticSyntheticGroup ? "Estimate" : "Predicts"}: <span className="font-medium">{formatBelief(belief)}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
              </div>
              )}

              {/* Right Panel - Conversation */}
              <div className="flex-1 min-w-0 min-h-[24rem] lg:min-h-0 flex flex-col">
                <div
                  ref={messagesContainerRef}
                  className="flex-1 min-h-0 border rounded-lg p-4 mb-3 overflow-y-auto"
                  onScroll={handleScroll}
                >
                  {stageMessages.map((m) => {
                      const isAI = m?.meta?.isAI;
                      const isMe = m?.subjectId === player.id;
                      const isSeededRationale = !!m?.meta?.seededRationale;
                      // Try to find the player object for this message sender
                      const messagePlayer = isAI ? null : players.find(p => p.id === m?.subjectId);
                      const syntheticPeer = isAI
                        ? null
                        : staticSyntheticPeers.find((peer) => peer.id === m?.subjectId);
                      const name = m?.subject?.name || (isAI ? "Assistant" : (messagePlayer ? getNicknameFromPlayer(messagePlayer) : syntheticPeer?.name || getNicknameFromId(m?.subjectId || "?")));
                      const animal = isAI
                        ? null
                        : (
                            messagePlayer
                              ? getAnimalFromPlayer(messagePlayer)
                              : syntheticPeer?.assignedAnimal ||
                                getAnimalFromId(m?.subjectId || "?")
                          );
                      const emoji = isAI ? "🤖" : getAnimalEmoji(animal);
                      return (
                        <div key={m?.id || `${m?.subjectId}-${m?.createdAt}`} className={`mb-3 flex ${isMe ? "justify-start" : "justify-end"}`}>
                          <div className={`max-w-[80%] flex gap-2 ${isMe ? "flex-row" : "flex-row-reverse"}`}>
                            <div className="h-8 w-8 rounded-full bg-white shadow flex items-center justify-center text-xl select-none">
                              <span aria-hidden>{emoji}</span>
                            </div>
                            <div>
                              <div className="text-xs text-gray-500 mb-1">{name} {isMe && '(You)'} <span className="text-[10px] ml-2">{m?.createdAt ? new Date(Number(m.createdAt)).toLocaleTimeString() : ""}</span></div>
                              <div className={`px-3 py-2 rounded-lg ${isMe ? "bg-blue-50 text-blue-900" : "bg-emerald-50 text-emerald-900"}`}>
                                {isSeededRationale && (
                                  <div className="text-[11px] font-semibold uppercase tracking-wide opacity-70 mb-1">
                                    {isBaseRateStaticSyntheticGroup
                                      ? isMe
                                        ? "Your rationale"
                                        : "Earlier participant response"
                                      : "Opening rationale"}
                                  </div>
                                )}
                                {m?.text || m?.content}
                                {isSeededRationale &&
                                  isBaseRateStaticSyntheticGroup &&
                                  !isMe &&
                                  syntheticPeer && (() => {
                                    const peerKey = String(syntheticPeer.id);
                                    const currentScore = peerConvincingnessRatings[peerKey];
                                    return (
                                      <div className="mt-3 border-t border-emerald-200 pt-3">
                                        <div className="text-xs font-semibold text-emerald-900 mb-2">
                                          How convincing do you find this reasoning?
                                        </div>
                                        <div className="flex flex-wrap items-center gap-2">
                                          <span className="text-[10px] text-gray-600 whitespace-nowrap">
                                            Not at all
                                          </span>
                                          {CONVINCINGNESS_SCALE.map((option) => {
                                            const selected =
                                              Number(currentScore) === option.value;
                                            return (
                                              <button
                                                key={option.value}
                                                type="button"
                                                aria-label={`${option.label} convincing`}
                                                aria-pressed={selected}
                                                className={`h-8 w-8 rounded-full border text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-emerald-300 ${
                                                  selected
                                                    ? "bg-emerald-600 border-emerald-700 text-white"
                                                    : "bg-white border-emerald-300 text-emerald-900 hover:bg-emerald-100"
                                                }`}
                                                onClick={() =>
                                                  handleRatePeer(syntheticPeer.id, option.value)
                                                }
                                              >
                                                {option.value}
                                              </button>
                                            );
                                          })}
                                          <span className="text-[10px] text-gray-600 whitespace-nowrap">
                                            Extremely
                                          </span>
                                        </div>
                                      </div>
                                    );
                                  })()}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  {/* Invisible element for auto-scrolling */}
                  <div ref={messagesEndRef} />
                </div>

                {/* Turn indicator */}
                {!isBaseRateStaticSyntheticGroup && turnTakingEnabled && !isPrepWindowActive && (
                  <div className={`mb-3 p-3 rounded-lg shrink-0 ${isMyTurn ? 'bg-green-100 border-2 border-green-400' : 'bg-gray-100 border border-gray-300'}`}>
                    <div className="text-sm font-medium">
                      {isMyTurn ? (
                        <span className="text-green-800">
                          ✓ It's your turn to speak
                          {(() => {
                            const elapsed = currentTime - turnStartTime;
                            const remaining = Math.max(0, 45000 - elapsed);
                            const seconds = Math.ceil(remaining / 1000);
                            return seconds > 0 ? ` (${seconds}s remaining)` : " (Time's up!)";
                          })()}
                        </span>
                      ) : (
                        <span className="text-gray-600">
                          Waiting for {currentTurnPlayerName || "another player"} to speak...
                        </span>
                      )}
                    </div>
                    {/* Next speaker reminder */}
                    {isNextSpeaker && !isMyTurn && (
                      <div className="mt-2 text-sm font-medium text-blue-700 bg-blue-50 p-2 rounded border border-blue-200">
                        🎯 You are the next speaker! Start writing your arguments.
                      </div>
                    )}
                  </div>
                )}

                {/* Message Input */}
                {!isBaseRateStaticSyntheticGroup && (
                  <div className="flex gap-2 mb-6 shrink-0">
                    <input
                      className="border rounded px-3 py-2 flex-1"
                      placeholder={isPrepWindowActive ? "Draft your first message during prep time" : "Type a message"}
                      data-integrity-field="discussion-message-input"
                      value={inputText}
                      onChange={(e) => {
                        // Allow editing anytime
                        setInputText(e.target.value);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                    />
                    <button
                      className={`px-4 py-2 rounded ${canSendMessage ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-400 text-gray-200 cursor-not-allowed'}`}
                      disabled={!canSendMessage}
                      onClick={handleSendMessage}
                    >Send</button>
                  </div>
                )}

                {isPrepWindowActive && !isBaseRateStaticSyntheticGroup && (
                  <div className="mb-6 text-sm text-gray-600 shrink-0">
                    You can write now, but sending is locked until the 2-minute prep window ends.
                  </div>
                )}

                {isBaseRateStaticSyntheticGroup && (
                  <div className="mt-2 shrink-0">
                    <label className="mb-4 flex items-start justify-center gap-3 rounded-lg border border-gray-300 bg-gray-50 px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        className="mt-1 h-4 w-4 rounded border-gray-300 text-empirica-600 focus:ring-empirica-400"
                        checked={hasReviewedGroupRationales}
                        onChange={handleReviewedGroupRationalesChange}
                      />
                      <span className="text-sm font-medium text-gray-900">
                        I have reviewed the other participants' responses.
                      </span>
                    </label>
                    {!hasRatedAllPeers && (
                      <div className="mb-2 text-center text-xs text-gray-600">
                        Please rate how convincing you find each earlier participant's reasoning before continuing.
                      </div>
                    )}
                    <div className="flex justify-center">
                      <button
                        type="button"
                        className={`px-8 py-3 rounded-md font-semibold shadow-sm border focus:outline-none focus:ring-2 ${
                          hasReviewedGroupRationales && hasRatedAllPeers
                            ? "border-empirica-500 text-white bg-empirica-600 hover:bg-empirica-700 focus:ring-empirica-300"
                            : "border-gray-300 bg-gray-300 text-gray-500 cursor-not-allowed focus:ring-gray-200"
                        }`}
                        disabled={!hasReviewedGroupRationales || !hasRatedAllPeers}
                        onClick={handleContinue}
                      >
                        Continue
                      </button>
                    </div>
                  </div>
                )}

                {tType !== "No-AI Control" && (
                  <div className="text-sm text-gray-500 mt-2 shrink-0">
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
